import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Clock, Save } from "lucide-react";
import { tzShortLabel } from "../lib/time";

// Common US-first list — covers the majority of the customer base.
// Users in other zones still see times converted correctly since we store UTC.
const OPTIONS = [
  { tz: "America/Chicago",     label: "Central — Chicago" },
  { tz: "America/New_York",    label: "Eastern — New York" },
  { tz: "America/Denver",      label: "Mountain — Denver" },
  { tz: "America/Phoenix",     label: "Mountain — Phoenix (no DST)" },
  { tz: "America/Los_Angeles", label: "Pacific — Los Angeles" },
  { tz: "America/Anchorage",   label: "Alaska — Anchorage" },
  { tz: "Pacific/Honolulu",    label: "Hawaii — Honolulu" },
  { tz: "Europe/London",       label: "UK — London" },
  { tz: "Europe/Berlin",       label: "Central Europe — Berlin" },
  { tz: "Asia/Kolkata",        label: "India — Kolkata" },
  { tz: "Asia/Tokyo",          label: "Japan — Tokyo" },
  { tz: "Australia/Sydney",    label: "Australia — Sydney" },
  { tz: "UTC",                 label: "UTC" },
];

export default function SiteTimezoneCard() {
  const { settings, refreshSettings } = useSettings();
  const [tz, setTz] = useState("America/Chicago");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.site_timezone) setTz(settings.site_timezone);
  }, [settings?.site_timezone]);

  const dirty = tz !== (settings?.site_timezone || "America/Chicago");
  const short = tzShortLabel(tz);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { site_timezone: tz });
      await refreshSettings?.();
      toast.success(`Site timezone set to ${tz}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="admin-timezone-card">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> // Site Timezone
          </div>
          <h3 className="font-display font-black uppercase text-xl mt-1">Canonical Time</h3>
          <p className="text-slate-500 text-xs mt-1.5 max-w-xl">
            Every date shown across the app renders in this timezone — dashboards,
            history, weight, nutrition, reminder emails. Members in other zones
            still see the same wall-clock reading so shared events line up.
          </p>
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
          Current: <span className="text-[#0D9488]">{short || tz}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[280px]">
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="timezone-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              {OPTIONS.map((o) => (
                <SelectItem key={o.tz} value={o.tz}>
                  {o.label} <span className="text-slate-500 ml-1 font-mono-num text-xs">({o.tz})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-11 px-4 uppercase tracking-widest text-xs font-mono-num font-semibold"
          data-testid="timezone-save-btn"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save timezone"}
        </Button>
      </div>
    </Card>
  );
}
